import { useState, useCallback } from 'react';
import { useDocumentStore } from '../../stores/documentStore';
import { PDFManipulator } from '../../core/PDFManipulator';
import { downloadUint8Array } from '../../utils/fileUtils';
import { useUIStore } from '../../stores/uiStore';
import './Dialogs.css';

interface SplitDialogProps {
  onClose: () => void;
}

export function SplitDialog({ onClose }: SplitDialogProps) {
  const { documents, activeDocumentId, totalPages } = useDocumentStore();
  const activeDoc = documents.find((d) => d.id === activeDocumentId);
  const [splitMode, setSplitMode] = useState<'range' | 'each' | 'custom'>('range');
  const [rangeStart, setRangeStart] = useState(1);
  const [rangeEnd, setRangeEnd] = useState(totalPages);
  const [customRanges, setCustomRanges] = useState('1-3, 4-6');
  const [isSplitting, setIsSplitting] = useState(false);

  const handleSplit = useCallback(async () => {
    if (!activeDoc?.arrayBuffer) return;
    setIsSplitting(true);

    try {
      let ranges: { start: number; end: number }[] = [];

      if (splitMode === 'range') {
        ranges = [{ start: rangeStart, end: rangeEnd }];
      } else if (splitMode === 'each') {
        ranges = Array.from({ length: totalPages }, (_, i) => ({
          start: i + 1,
          end: i + 1,
        }));
      } else if (splitMode === 'custom') {
        ranges = customRanges.split(',').map((r) => {
          const parts = r.trim().split('-').map(Number);
          return { start: parts[0], end: parts[1] || parts[0] };
        });
      }

      const results = await PDFManipulator.split({
        source: activeDoc.arrayBuffer,
        ranges,
      });

      results.forEach((data, i) => {
        const range = ranges[i];
        const filename = `${activeDoc.filename.replace('.pdf', '')}_pages_${range.start}-${range.end}.pdf`;
        downloadUint8Array(data, filename);
      });

      useUIStore.getState().addToast({
        type: 'success',
        message: `Split into ${results.length} file(s)!`,
      });
      onClose();
    } catch (err) {
      console.error('Split failed:', err);
      useUIStore.getState().addToast({ type: 'error', message: 'Failed to split PDF' });
    } finally {
      setIsSplitting(false);
    }
  }, [activeDoc, splitMode, rangeStart, rangeEnd, customRanges, totalPages, onClose]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: 460 }}>
        <div className="modal-header">
          <h3>✂️ Split PDF</h3>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <p className="dialog-description">
            Split "{activeDoc?.filename}" ({totalPages} pages) into separate files.
          </p>

          <div className="split-modes">
            <label className={`split-mode-option ${splitMode === 'range' ? 'active' : ''}`}>
              <input type="radio" name="splitMode" checked={splitMode === 'range'} onChange={() => setSplitMode('range')} />
              <div>
                <strong>Extract Range</strong>
                <span>Extract a range of pages</span>
              </div>
            </label>

            <label className={`split-mode-option ${splitMode === 'each' ? 'active' : ''}`}>
              <input type="radio" name="splitMode" checked={splitMode === 'each'} onChange={() => setSplitMode('each')} />
              <div>
                <strong>Split Every Page</strong>
                <span>Each page becomes a separate file</span>
              </div>
            </label>

            <label className={`split-mode-option ${splitMode === 'custom' ? 'active' : ''}`}>
              <input type="radio" name="splitMode" checked={splitMode === 'custom'} onChange={() => setSplitMode('custom')} />
              <div>
                <strong>Custom Ranges</strong>
                <span>Define specific page ranges</span>
              </div>
            </label>
          </div>

          {splitMode === 'range' && (
            <div className="split-range-inputs">
              <div className="input-group">
                <label>From page</label>
                <input className="input" type="number" min={1} max={totalPages} value={rangeStart} onChange={(e) => setRangeStart(Number(e.target.value))} />
              </div>
              <div className="input-group">
                <label>To page</label>
                <input className="input" type="number" min={rangeStart} max={totalPages} value={rangeEnd} onChange={(e) => setRangeEnd(Number(e.target.value))} />
              </div>
            </div>
          )}

          {splitMode === 'custom' && (
            <div className="input-group" style={{ marginTop: 'var(--space-4)' }}>
              <label>Page ranges (e.g., 1-3, 4-6, 7-10)</label>
              <input className="input" type="text" value={customRanges} onChange={(e) => setCustomRanges(e.target.value)} placeholder="1-3, 4-6, 7-10" />
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSplit} disabled={isSplitting}>
            {isSplitting ? '⏳ Splitting...' : 'Split PDF'}
          </button>
        </div>
      </div>
    </div>
  );
}
